/**
 * A minimal, dependency-free XLSX writer.
 *
 * An .xlsx file is a ZIP of XML parts. Writing the handful of parts Excel needs
 * is a couple of hundred lines, which is a better trade than pulling a
 * multi-megabyte spreadsheet library into an admin dashboard that only ever
 * writes one flat sheet of strings and numbers.
 *
 * Entries are stored uncompressed (ZIP method 0), which Excel, LibreOffice,
 * Numbers and Google Sheets all accept.
 */

export type CellValue = string | number | null | undefined

/* ------------------------------------------------------------------ CRC32 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let index = 0; index < 256; index += 1) {
    let value = index
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1
    }
    table[index] = value >>> 0
  }
  return table
})()

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

/* -------------------------------------------------------------- ZIP writer */

interface ZipEntry {
  name: string
  data: Uint8Array
  crc: number
  offset: number
}

const encoder = new TextEncoder()

class ByteWriter {
  private chunks: Uint8Array[] = []
  private length = 0

  get size(): number {
    return this.length
  }

  push(bytes: Uint8Array): void {
    this.chunks.push(bytes)
    this.length += bytes.length
  }

  uint16(value: number): void {
    this.push(new Uint8Array([value & 0xff, (value >>> 8) & 0xff]))
  }

  uint32(value: number): void {
    this.push(new Uint8Array([value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff]))
  }

  toUint8Array(): Uint8Array {
    const output = new Uint8Array(this.length)
    let offset = 0
    for (const chunk of this.chunks) {
      output.set(chunk, offset)
      offset += chunk.length
    }
    return output
  }
}

/** A fixed timestamp keeps the output byte-identical for identical input. */
const DOS_TIME = 0
const DOS_DATE = 0x2821 // 2000-01-01

export function zipParts(files: { name: string; content: string }[]): Uint8Array {
  const writer = new ByteWriter()
  const entries: ZipEntry[] = []

  for (const file of files) {
    const data = encoder.encode(file.content)
    const nameBytes = encoder.encode(file.name)
    const entry: ZipEntry = { name: file.name, data, crc: crc32(data), offset: writer.size }
    entries.push(entry)

    writer.uint32(0x04034b50) // local file header
    writer.uint16(20) // version needed
    writer.uint16(0x0800) // UTF-8 file names
    writer.uint16(0) // stored, not deflated
    writer.uint16(DOS_TIME)
    writer.uint16(DOS_DATE)
    writer.uint32(entry.crc)
    writer.uint32(data.length) // compressed size == uncompressed size
    writer.uint32(data.length)
    writer.uint16(nameBytes.length)
    writer.uint16(0) // no extra field
    writer.push(nameBytes)
    writer.push(data)
  }

  const centralDirectoryOffset = writer.size

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name)

    writer.uint32(0x02014b50) // central directory header
    writer.uint16(20) // version made by
    writer.uint16(20) // version needed
    writer.uint16(0x0800)
    writer.uint16(0)
    writer.uint16(DOS_TIME)
    writer.uint16(DOS_DATE)
    writer.uint32(entry.crc)
    writer.uint32(entry.data.length)
    writer.uint32(entry.data.length)
    writer.uint16(nameBytes.length)
    writer.uint16(0) // extra
    writer.uint16(0) // comment
    writer.uint16(0) // disk number
    writer.uint16(0) // internal attributes
    writer.uint32(0) // external attributes
    writer.uint32(entry.offset)
    writer.push(nameBytes)
  }

  const centralDirectorySize = writer.size - centralDirectoryOffset

  writer.uint32(0x06054b50) // end of central directory
  writer.uint16(0)
  writer.uint16(0)
  writer.uint16(entries.length)
  writer.uint16(entries.length)
  writer.uint32(centralDirectorySize)
  writer.uint32(centralDirectoryOffset)
  writer.uint16(0) // no comment

  return writer.toUint8Array()
}

export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/* --------------------------------------------------------------- sheet XML */

/**
 * Control characters are illegal in XML 1.0, and a single one anywhere in the
 * data makes Excel reject the whole workbook as corrupt. Built with `RegExp`
 * so the source file holds no literal control bytes.
 */
// eslint-disable-next-line no-control-regex -- stripping control characters is the point
const INVALID_XML_CHARS = new RegExp('[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F]', 'g')

function escapeXml(value: string): string {
  return value
    .replace(INVALID_XML_CHARS, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/** 0 -> A, 25 -> Z, 26 -> AA. */
export function columnLetter(index: number): string {
  let letters = ''
  let remaining = index
  while (remaining >= 0) {
    letters = String.fromCharCode((remaining % 26) + 65) + letters
    remaining = Math.floor(remaining / 26) - 1
  }
  return letters
}

function cellXml(reference: string, value: CellValue, isHeader: boolean): string {
  const style = isHeader ? ' s="1"' : ''

  if (value === null || value === undefined || value === '') {
    return `<c r="${reference}"${style}/>`
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${reference}"${style}><v>${value}</v></c>`
  }
  return `<c r="${reference}" t="inlineStr"${style}><is><t xml:space="preserve">${escapeXml(String(value))}</t></is></c>`
}

/**
 * Builds a single-sheet workbook.
 *
 * The header row is bold and frozen so a long export stays readable when
 * scrolled, which is the whole reason to offer Excel over CSV.
 */
export function buildXlsxBytes(headers: string[], rows: CellValue[][], sheetName = 'Export'): Uint8Array {
  const allRows: CellValue[][] = [headers, ...rows]

  const sheetRows = allRows
    .map((row, rowIndex) => {
      const cells = row
        .map((value, columnIndex) => cellXml(`${columnLetter(columnIndex)}${rowIndex + 1}`, value, rowIndex === 0))
        .join('')
      return `<row r="${rowIndex + 1}">${cells}</row>`
    })
    .join('')

  // Excel rejects a sheet name over 31 characters or containing : \ / ? * [ ]
  const safeSheetName = escapeXml(sheetName.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31)) || 'Export'

  const columnWidths = headers
    .map(
      (header, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${Math.min(
          Math.max(header.length + 6, 12),
          42,
        )}" customWidth="1"/>`,
    )
    .join('')

  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="15"/><cols>${columnWidths}</cols><sheetData>${sheetRows}</sheetData></worksheet>`

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${safeSheetName}" sheetId="1" r:id="rId1"/></sheets></workbook>`

  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs></styleSheet>`

  return zipParts([
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    { name: 'xl/workbook.xml', content: workbook },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    { name: 'xl/styles.xml', content: styles },
    { name: 'xl/worksheets/sheet1.xml', content: sheet },
  ])
}

/** Browser-facing wrapper: the same bytes, ready to download. */
export function buildXlsx(headers: string[], rows: CellValue[][], sheetName = 'Export'): Blob {
  return new Blob([buildXlsxBytes(headers, rows, sheetName)], { type: XLSX_MIME_TYPE })
}
